// RAPIFAST :: Datos y lógica FICTICIOS de demostración para el flujo de verificación
// del piloto (El Cerrito). NO provienen de la base de datos real ni de Supabase.
// Los archivos adjuntados son archivos de prueba manejados SOLO en el navegador
// (no hay almacenamiento seguro conectado). Al autorizar 0024/0025 esto se
// reemplaza por RPCs + Storage privado con URL firmada.
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const today = () => iso(new Date());
export const daysFromNow = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return iso(d);
};

export const SELF_DRIVER_ID = "drv-demo-self";
export const SELF_BUSINESS_ID = "biz-demo-self";

export const DOC_LABELS = {
  CEDULA: "Documento de identidad",
  LICENCIA: "Licencia de conducción",
  SOAT: "SOAT",
  TARJETA_PROPIEDAD: "Licencia de tránsito / Tarjeta de propiedad",
  TECNOMECANICA: "Revisión técnico-mecánica",
  COINCIDENCIA: "Coincidencia persona-vehículo",
};
export const DRIVER_DOC_ORDER = ["CEDULA", "LICENCIA", "SOAT", "TARJETA_PROPIEDAD", "TECNOMECANICA"];
export const DOC_WITH_EXPIRY = ["LICENCIA", "SOAT", "TECNOMECANICA"];
// Documentos que el conductor adjunta como archivo (demo).
export const DOC_WITH_FILE = ["CEDULA", "LICENCIA", "SOAT", "TARJETA_PROPIEDAD", "TECNOMECANICA"];

export const DOC_STATUS = {
  NOT_UPLOADED: { label: "Sin cargar", cls: "text-gray-400 bg-gray-500/10 border-gray-500/40" },
  PENDING: { label: "Pendiente de revisión", cls: "text-amber-400 bg-amber-500/10 border-amber-500/40" },
  VERIFIED: { label: "Verificado", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/40" },
  REJECTED: { label: "Rechazado", cls: "text-red-400 bg-red-500/10 border-red-500/40" },
  NOT_APPLICABLE: { label: "No aplica", cls: "text-gray-400 bg-gray-500/10 border-gray-500/40" },
};

export const VERIF_STATUS = {
  PENDING: { label: "Pendiente", cls: "text-blue-400 bg-blue-500/10 border-blue-500/40" },
  IN_REVIEW: { label: "En revisión", cls: "text-amber-400 bg-amber-500/10 border-amber-500/40" },
  APPROVED: { label: "Aprobado / Habilitado", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/40" },
  REJECTED: { label: "Rechazado", cls: "text-red-400 bg-red-500/10 border-red-500/40" },
  EXPIRED: { label: "Vencido", cls: "text-red-400 bg-red-600/15 border-red-600/50" },
};

export const SEMAFORO = {
  GREEN: { label: "Habilitado", dot: "#10B981", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/40" },
  YELLOW: { label: "Por vencer", dot: "#F59E0B", cls: "text-amber-400 bg-amber-500/10 border-amber-500/40" },
  RED: { label: "Inhabilitado", dot: "#EF4444", cls: "text-red-400 bg-red-500/10 border-red-500/40" },
};

export const isExpired = (doc) => !!doc?.expiresAt && doc.expiresAt < today();
export const expiringSoon = (doc, days = 15) =>
  !!doc?.expiresAt && !isExpired(doc) && doc.expiresAt <= daysFromNow(days);

const mandatoryKeys = (d) => {
  const base = ["CEDULA", "LICENCIA", "SOAT", "TARJETA_PROPIEDAD"];
  if (d.vehicle?.type !== "MOTOCICLETA") base.push("TECNOMECANICA");
  return base;
};

// Regla del piloto (espejo de 0024): obligatorios VERIFICADOS por el admin y vigentes.
// Adjuntar NO equivale a verificar.
export function driverRule(d) {
  const missing = mandatoryKeys(d).filter((k) => {
    const doc = d.docs[k];
    return !doc || doc.status !== "VERIFIED" || isExpired(doc);
  });
  return { ok: missing.length === 0, missing };
}

export function driverDisplayStatus(d) {
  if (["CEDULA", "LICENCIA", "SOAT", "TARJETA_PROPIEDAD", "TECNOMECANICA"].some((k) => d.docs[k] && isExpired(d.docs[k])))
    return "EXPIRED";
  return d.status;
}

export function driverSemaforo(d) {
  if (!driverRule(d).ok) return "RED";
  if (["LICENCIA", "SOAT", "TECNOMECANICA"].some((k) => expiringSoon(d.docs[k]))) return "YELLOW";
  return "GREEN";
}

export function businessSemaforo(b) {
  if (b.status === "REJECTED") return "RED";
  const done = b.checks.filter(Boolean).length;
  if (b.status === "APPROVED" && done === b.checks.length) return "GREEN";
  if (done >= 5) return "YELLOW";
  return "RED";
}

export const DRIVER_DECLARATIONS = [
  "Declara que la información suministrada es verdadera y se compromete a informar cambios relevantes.",
  "Entiende que RAPIFAST se destina a la entrega de productos y paquetes, no al transporte de pasajeros.",
  "Se compromete a respetar las normas de tránsito, cuidar los paquetes y reportar incidentes.",
  "Entiende que no debe aceptar mercancías ilegales, peligrosas no admitidas o solicitudes riesgosas.",
  "Confirma que recibió acceso o información sobre los términos, reglas operativas y canales de soporte.",
];

export const BUSINESS_ACCEPTANCES = [
  "Recibió información sobre la tarifa antes de solicitar el servicio.",
  "Entiende que el servicio cubre entrega de productos y paquetes, no transporte de pasajeros.",
  "Conoce las restricciones de mercancías, embalaje y seguridad.",
  "Conoce el procedimiento para cancelar, reportar novedades y presentar reclamaciones.",
  "Entiende que los tiempos de entrega no se garantizan salvo política aprobada.",
];

export const BUSINESS_CHECKS = [
  "Datos de contacto confirmados",
  "Dirección de recogida verificada o instrucciones suficientes",
  "Persona autorizada identificada",
  "Tipos de productos y restricciones explicados",
  "Tarifas y procedimiento de pago comunicados",
  "Canal de soporte e incidentes informado",
  "Política de datos y aviso entregados o puestos a disposición",
  "Acuerdo comercial revisado y aceptado (solo tras aprobación legal)",
];

const doc = (status, expiresAt = null, notes = "", file = null) => ({ status, expiresAt, notes, file });
const emptyDoc = () => doc("NOT_UPLOADED");

// Registro propio del conductor/negocio logueado (arranca vacío para probar la carga).
export function buildSelfDriver() {
  return {
    id: SELF_DRIVER_ID, self: true,
    name: "Mi registro de conductor (demo)",
    docType: "CC", docNumber: "", phone: "", email: "", zone: "El Cerrito, Valle del Cauca",
    birthDate: "", emergencyContact: "",
    vehicle: { type: "MOTOCICLETA", plate: "", brandModel: "", ownerName: "", capacity: "" },
    docs: {
      CEDULA: emptyDoc(), LICENCIA: emptyDoc(), SOAT: emptyDoc(),
      TARJETA_PROPIEDAD: emptyDoc(), TECNOMECANICA: doc("NOT_APPLICABLE"), COINCIDENCIA: emptyDoc(),
    },
    payout: { method: "Nequi", holder: "", account: "" },
    confirmations: [false, false, false, false, false],
    privacyRef: "Aviso de privacidad borrador v1.0 (demo)",
    status: "PENDING", reviewNotes: "", reviewedBy: "", reviewedAt: "",
  };
}

export function buildSelfBusiness() {
  return {
    id: SELF_BUSINESS_ID, self: true,
    commercialName: "Mi negocio aliado (demo)", legalName: "", nit: "",
    activityType: "", address: "", municipality: "El Cerrito, Valle del Cauca",
    phone: "", email: "", schedule: "",
    responsible: { name: "", doc: "", phone: "" },
    authorizedDispatchers: "",
    ops: { pickupAddress: "", references: "", productTypes: "", dimensionsWeight: "", specialHandling: "", volume: "Por definir", dispatcher: "" },
    acceptances: [false, false, false, false, false],
    agreementAccepted: false, agreementAcceptedAt: "",
    paymentNote: "Pendiente definir método de pago (demo)",
    checks: [false, false, false, false, false, false, false, false],
    supports: [], // soportes opcionales adjuntos por el negocio (demo)
    privacyRef: "Pendiente entregar aviso (demo)",
    status: "PENDING", reviewNotes: "", reviewedBy: "", reviewedAt: "",
  };
}

export const MOCK_DRIVERS = [
  {
    id: "drv-demo-1", name: "Demo Conductor Habilitado",
    docType: "CC", docNumber: "1110000001", phone: "3000000001", email: "demo.conductor1@example.test",
    zone: "El Cerrito, Valle del Cauca", birthDate: "", emergencyContact: "",
    vehicle: { type: "MOTOCICLETA", plate: "DEM-01F", brandModel: "Yamaha FZ 150 · 2022", ownerName: "El mismo conductor", capacity: "Hasta 25 kg / baúl 45L" },
    docs: {
      CEDULA: doc("VERIFIED"), LICENCIA: doc("VERIFIED", daysFromNow(300)),
      SOAT: doc("VERIFIED", daysFromNow(200)), TARJETA_PROPIEDAD: doc("VERIFIED"),
      TECNOMECANICA: doc("NOT_APPLICABLE"), COINCIDENCIA: doc("VERIFIED"),
    },
    payout: { method: "Nequi", holder: "Demo Conductor Habilitado", account: "3000000001" },
    confirmations: [true, true, true, true, true], privacyRef: "Aviso de privacidad borrador v1.0 (demo)",
    status: "APPROVED", reviewNotes: "", reviewedBy: "Admin demo", reviewedAt: daysFromNow(-3),
  },
  {
    id: "drv-demo-2", name: "Demo Conductora Por Vencer",
    docType: "CC", docNumber: "1110000002", phone: "3000000002", email: "demo.conductor2@example.test",
    zone: "El Cerrito, Valle del Cauca", birthDate: "1995-04-12", emergencyContact: "Familiar demo · 3110000002",
    vehicle: { type: "MOTOCICLETA", plate: "DEM-02F", brandModel: "Honda CB 125 · 2021", ownerName: "La misma conductora", capacity: "Hasta 20 kg / baúl 38L" },
    docs: {
      CEDULA: doc("VERIFIED"), LICENCIA: doc("VERIFIED", daysFromNow(120)),
      SOAT: doc("VERIFIED", daysFromNow(9)), TARJETA_PROPIEDAD: doc("VERIFIED"),
      TECNOMECANICA: doc("NOT_APPLICABLE"), COINCIDENCIA: doc("VERIFIED"),
    },
    payout: { method: "Bancolombia", holder: "Demo Conductora Por Vencer", account: "000-111-222" },
    confirmations: [true, true, true, true, true], privacyRef: "Aviso de privacidad borrador v1.0 (demo)",
    status: "APPROVED", reviewNotes: "", reviewedBy: "Admin demo", reviewedAt: daysFromNow(-10),
  },
  {
    id: "drv-demo-3", name: "Demo Conductor Incompleto",
    docType: "CC", docNumber: "1110000003", phone: "3000000003", email: "demo.conductor3@example.test",
    zone: "Ginebra, Valle del Cauca", birthDate: "", emergencyContact: "",
    vehicle: { type: "CAMIONETA", plate: "DEM-03F", brandModel: "Chevrolet N300 · 2019", ownerName: "Tercero (demo)", capacity: "Hasta 300 kg" },
    docs: {
      CEDULA: doc("VERIFIED"), LICENCIA: doc("PENDING", daysFromNow(150)),
      SOAT: doc("NOT_UPLOADED"), TARJETA_PROPIEDAD: doc("VERIFIED"),
      TECNOMECANICA: doc("NOT_UPLOADED"), COINCIDENCIA: doc("NOT_UPLOADED"),
    },
    payout: { method: "Efectivo", holder: "Demo Conductor Incompleto", account: "—" },
    confirmations: [true, true, false, false, false], privacyRef: "Pendiente entregar aviso (demo)",
    status: "IN_REVIEW", reviewNotes: "", reviewedBy: "", reviewedAt: "",
  },
  {
    id: "drv-demo-4", name: "Demo Conductor Doc Vencido",
    docType: "CC", docNumber: "1110000004", phone: "3000000004", email: "demo.conductor4@example.test",
    zone: "El Cerrito, Valle del Cauca", birthDate: "", emergencyContact: "",
    vehicle: { type: "MOTOCICLETA", plate: "DEM-04F", brandModel: "Suzuki GN 125 · 2018", ownerName: "El mismo conductor", capacity: "Hasta 20 kg" },
    docs: {
      CEDULA: doc("VERIFIED"), LICENCIA: doc("VERIFIED", daysFromNow(-5)),
      SOAT: doc("VERIFIED", daysFromNow(90)), TARJETA_PROPIEDAD: doc("VERIFIED"),
      TECNOMECANICA: doc("NOT_APPLICABLE"), COINCIDENCIA: doc("VERIFIED"),
    },
    payout: { method: "Nequi", holder: "Demo Conductor Doc Vencido", account: "3000000004" },
    confirmations: [true, true, true, true, true], privacyRef: "Aviso de privacidad borrador v1.0 (demo)",
    status: "APPROVED", reviewNotes: "", reviewedBy: "Admin demo", reviewedAt: daysFromNow(-40),
  },
  {
    id: "drv-demo-5", name: "Demo Conductora Rechazada",
    docType: "CC", docNumber: "1110000005", phone: "3000000005", email: "demo.conductor5@example.test",
    zone: "El Cerrito, Valle del Cauca", birthDate: "", emergencyContact: "",
    vehicle: { type: "MOTOCICLETA", plate: "DEM-05F", brandModel: "AKT NKD 125 · 2020", ownerName: "La misma conductora", capacity: "Hasta 15 kg" },
    docs: {
      CEDULA: doc("VERIFIED"), LICENCIA: doc("REJECTED", daysFromNow(300), "Licencia no legible (demo)"),
      SOAT: doc("VERIFIED", daysFromNow(60)), TARJETA_PROPIEDAD: doc("VERIFIED"),
      TECNOMECANICA: doc("NOT_APPLICABLE"), COINCIDENCIA: doc("PENDING"),
    },
    payout: { method: "Otro", holder: "Demo Conductora Rechazada", account: "—" },
    confirmations: [true, true, true, false, true], privacyRef: "Aviso de privacidad borrador v1.0 (demo)",
    status: "REJECTED", reviewNotes: "Licencia no legible; se solicitó nuevo soporte (demo).", reviewedBy: "Admin demo", reviewedAt: daysFromNow(-2),
  },
];

export const MOCK_BUSINESSES = [
  {
    id: "biz-demo-1", commercialName: "Demo Ferretería El Tornillo", legalName: "Ferretería Demo S.A.S.", nit: "900.000.001-1",
    activityType: "Ferretería / Construcción", address: "Calle 1 # 1-01 (demo)", municipality: "El Cerrito, Valle del Cauca",
    phone: "6020000001", email: "demo.negocio1@example.test", schedule: "Lun–Sáb 8:00–18:00",
    responsible: { name: "Responsable Demo Uno", doc: "1111000001", phone: "3010000001" },
    authorizedDispatchers: "Mostrador demo: persona autorizada 1 y 2",
    ops: { pickupAddress: "", references: "Frente al parque (demo)", productTypes: "Herramientas y tornillería empacada", dimensionsWeight: "2–15 kg, máx. 50×50×50 cm", specialHandling: "No mojar", volume: "Diario", dispatcher: "Encargado de mostrador (demo)" },
    acceptances: [true, true, true, true, true], agreementAccepted: true, agreementAcceptedAt: daysFromNow(-2),
    paymentNote: "Pendiente definir método de pago (demo)",
    checks: [true, true, true, true, true, true, true, true], supports: [],
    privacyRef: "Aviso de privacidad borrador v1.0 (demo)",
    status: "APPROVED", reviewNotes: "", reviewedBy: "Admin demo", reviewedAt: daysFromNow(-1),
  },
  {
    id: "biz-demo-2", commercialName: "Demo Droguería La Central", legalName: "Droguería Demo Ltda.", nit: "900.000.002-2",
    activityType: "Farmacia / Salud", address: "Carrera 2 # 2-02 (demo)", municipality: "El Cerrito, Valle del Cauca",
    phone: "6020000002", email: "demo.negocio2@example.test", schedule: "Lun–Vie 9:00–19:00",
    responsible: { name: "Responsable Demo Dos", doc: "1111000002", phone: "3010000002" },
    authorizedDispatchers: "Auxiliar demo",
    ops: { pickupAddress: "Bodega trasera (demo)", references: "", productTypes: "Paquetería de droguería (no refrigerados)", dimensionsWeight: "1–5 kg", specialHandling: "Frágil", volume: "Varias al día", dispatcher: "" },
    acceptances: [true, true, true, false, false], agreementAccepted: false, agreementAcceptedAt: "",
    paymentNote: "Pendiente definir método de pago (demo)",
    checks: [true, true, true, true, true, true, false, false], supports: [],
    privacyRef: "Pendiente entregar aviso (demo)",
    status: "IN_REVIEW", reviewNotes: "", reviewedBy: "", reviewedAt: "",
  },
  {
    id: "biz-demo-3", commercialName: "Demo Papelería Estudio", legalName: "Papelería Demo", nit: "1.111.000.003-3",
    activityType: "Papelería / Variedades", address: "Calle 3 # 3-03 (demo)", municipality: "El Cerrito, Valle del Cauca",
    phone: "6020000003", email: "demo.negocio3@example.test", schedule: "Lun–Sáb 8:00–17:00",
    responsible: { name: "Responsable Demo Tres", doc: "1111000003", phone: "3010000003" },
    authorizedDispatchers: "",
    ops: { pickupAddress: "", references: "", productTypes: "Útiles y papelería", dimensionsWeight: "0.5–3 kg", specialHandling: "", volume: "Ocasional", dispatcher: "" },
    acceptances: [false, false, false, false, false], agreementAccepted: false, agreementAcceptedAt: "",
    paymentNote: "Pendiente definir método de pago (demo)",
    checks: [true, true, false, false, false, false, false, false], supports: [],
    privacyRef: "Pendiente (demo)",
    status: "PENDING", reviewNotes: "", reviewedBy: "", reviewedAt: "",
  },
  {
    id: "biz-demo-4", commercialName: "Demo Repuestos Rápidos", legalName: "Repuestos Demo S.A.S.", nit: "900.000.004-4",
    activityType: "Repuestos / Automotriz", address: "Carrera 4 # 4-04 (demo)", municipality: "El Cerrito, Valle del Cauca",
    phone: "6020000004", email: "demo.negocio4@example.test", schedule: "Lun–Sáb 7:00–18:00",
    responsible: { name: "Responsable Demo Cuatro", doc: "1111000004", phone: "3010000004" },
    authorizedDispatchers: "Bodega demo",
    ops: { pickupAddress: "", references: "", productTypes: "Repuestos pequeños", dimensionsWeight: "2–20 kg", specialHandling: "", volume: "Diario", dispatcher: "" },
    acceptances: [true, true, false, true, false], agreementAccepted: false, agreementAcceptedAt: "",
    paymentNote: "Pendiente definir método de pago (demo)",
    checks: [true, false, true, false, false, false, false, false], supports: [],
    privacyRef: "Pendiente (demo)",
    status: "REJECTED", reviewNotes: "Dirección de recogida no verificada; contacto sin respuesta (demo).", reviewedBy: "Admin demo", reviewedAt: daysFromNow(-4),
  },
];
